import {
  API,
  ErrorHandling,
  EVENT_APP_LOADING_COMPLETE,
  EVENT_APP_TERMINATING,
  executeWithRetry,
  FatalErrors,
  fetchWrapper,
  getIslandType,
  getServerRoomName,
  getUpdatedLauncherStartupOptions,
  iFrameLauncherEvent,
  type iFrameLauncherMessage,
  LauncherEventType,
  Logger,
  mocksResponse,
  type OnAppTerminatingData,
} from "@classdojo/monsterverse-utils";
import { IslandType } from "@monsterverse/island-types";
import { decode, encode } from "@msgpack/msgpack";
import { Client, type Room } from "colyseus.js";

import type { PCAppEventHandler } from "./PCAppEventHandler";

const pingTimerInSeconds = 30;
const noActivityTimeoutInSeconds = 600;
const backgroundTimeoutInSeconds = 30;
const reconnectionDelayInSeconds = 1;
const reconnectionDelayVarianceInSeconds = 3;
const totalReconnectionAttempts = 5;

export class Networking {
  private _client?: Client;
  private _room?: Room<unknown>;
  private _URL: URL;

  private _receivedMessage = false;
  private _reconnectAttemptTimeout?: number;
  private _reconnectionAttemptsLeft = totalReconnectionAttempts;
  private _connected = false;

  private _timeoutTimer?: number;
  private _backgroundTimeoutTimer?: number;
  private _skipNextTimeoutReset = false;

  private _perfTier = -1;
  private _pingInterval?: number;
  private _pingSentTime = Date.now();
  private _receivedMessageCount = 0;
  private _sentMessageCount = 0;

  public loadingComplete = false;

  private _originalGroupId: string;
  private _originalTicket: string;

  constructor(
    private _iframeEl: HTMLIFrameElement,
    pcAppEventHandler: PCAppEventHandler,
    wsUrl: URL,
    private _groupId: string,
    private _countryCode: string,
    private _serverRegion: string,
    private _desiredServerRegion: string
  ) {
    this._originalGroupId = _groupId;
    this._originalTicket = wsUrl.searchParams.get("ticket") ?? "";

    window.addEventListener("message", this._onIFrameMessage.bind(this));

    pcAppEventHandler.on(EVENT_APP_LOADING_COMPLETE, () => {
      this.loadingComplete = true;
      this._resetInactivityTimer();
    });

    pcAppEventHandler.on(EVENT_APP_TERMINATING, (data: unknown) => {
      const handler = data as OnAppTerminatingData;
      if (handler) {
        const { exitingMonsterverse, customizer } = handler;
        if (!exitingMonsterverse && customizer) {
          this.loadingComplete = false;
          this._clearTimeoutTimer();
          this._clearBackgroundInactivityTimer();
        }
      }
    });

    this._URL = wsUrl;
    this._connect(false);
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  private async _validateTicketAndConnect() {
    const env = window._ENV || "local";
    if (env !== "local") {
      //Build the check ticket url
      const checkTicketUrl = new URL(this._URL.toString());
      checkTicketUrl.protocol = "https:";
      checkTicketUrl.pathname = "/checkTicket";

      let invalidTicket = false;
      try {
        //Check our ticket
        const response = await fetchWrapper("ticketCheck", checkTicketUrl.toString(), { method: "GET" });

        //Failed response
        if (!response.ok) {
          const responseText = await response.text();
          invalidTicket = responseText === "Invalid server ticket";
          throw new Error(`ticketCheck: status failure ${responseText}`);
        }

        //Update ticket with refreshed value
        try {
          const result = await response.json();
          this._URL.searchParams.set("ticket", result.ticket);
        } catch {
          throw new Error(`ticketCheck: failed to decode json response`);
        }
      } catch (e) {
        this._reconnectionAttemptsLeft--;
        if (this._reconnectionAttemptsLeft <= 0 || invalidTicket) {
          if (e instanceof Error && "type" in e && e.type === "FetchError") {
            Logger.logInfo("ticketCheck: Failed to fetch the route");
          } else if (e instanceof Error) {
            Logger.logInfo(e.message);
          } else {
            Logger.logInfo("ticketCheck: Unknown failure");
          }
          this._disconnect(invalidTicket ? FatalErrors.ServerTicketExpiredTimeout : FatalErrors.ServerReconnectTimeout);
        } else {
          this._startReconnectAttemptTimer();
        }

        //Prevent connection from happening below
        return;
      }
    }

    this._connect(true);
  }

  private _connect(reconnecting: boolean) {
    this._client = new Client(this._URL.toString());

    const connectOptions: Record<string, string> = {};
    for (const [key, value] of this._URL.searchParams.entries()) {
      connectOptions[key] = value;
    }

    //Overwrite the endpoint functions to support params (ticket and logger session id)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const origHttpEndpoint = (this._client as any).getHttpEndpoint.bind(this._client);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this._client as any).getHttpEndpoint = (segments = "") => {
      return `${origHttpEndpoint(segments)}?${this._URL.searchParams.toString()}`;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const origWsEndpoint = (this._client as any).buildEndpoint.bind(this._client);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this._client as any).buildEndpoint = (room: any, options: any = {}) => {
      return origWsEndpoint(room, { ...options, ...connectOptions });
    };

    //Remove headers to prevent CORS requests that we don't need
    const origHttpPost = this._client.http.post.bind(this._client.http);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this._client.http.post = (path: string, options: any = {}) => {
      delete options["headers"];
      return origHttpPost(path, options);
    };

    this._client
      .joinOrCreate(getServerRoomName(this._groupId), connectOptions)
      .then((room) => this._onJoin(room, reconnecting))
      .catch((reason) => {
        this._reconnectionAttemptsLeft--;
        if (this._reconnectionAttemptsLeft <= 0) {
          if (reason instanceof Error) {
            if ("code" in reason) {
              Logger.logInfo(`Colyseus error: ${reason.name} code ${reason.code} - ${reason.message}`);
            } else {
              Logger.logInfo(`Colyseus error: ${reason.name} - ${reason.message}`);
            }
          } else {
            Logger.logInfo(`Colyseus error: unknown type - ${JSON.stringify(reason)}`);
          }

          Logger.logPerfStats({
            type: "NetworkingConnection",
            site: "launcher",
            status: "disconnected",
          });

          this._disconnect(FatalErrors.WebsocketClosure);
        } else {
          this._startReconnectAttemptTimer();
        }
      });

    this._receivedMessage = false;
  }

  private _onJoin(room: Room<unknown>, reconnecting: boolean) {
    this._room = room;

    this._room.onMessage("*", (_t, message) => {
      if (!this._receivedMessage) {
        Logger.logEvent("monsterverse.launcher.websocketConnected");
        this._receivedMessage = true;
      }

      this._postMessage(LauncherEventType.NetworkMessage, message);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const decoded = decode(message) as any;
      const isLauncher = "launcher" in decoded;
      if (isLauncher) {
        this._onMessage(decoded);
      }
    });

    this._room.onLeave((_code) => {
      if (this._pingInterval) {
        clearInterval(this._pingInterval);
        this._pingInterval = undefined;
      }

      this._postMessage(LauncherEventType.NetworkLosing);
      this._connected = false;

      Logger.logPerfStats({
        type: "NetworkingConnection",
        site: "launcher",
        status: "lost",
      });
      this._startReconnectAttemptTimer();
    });

    console.log("websocket is open, sending join");
    Logger.logEvent("monsterverse.launcher.websocketOpen");
    Logger.logPerfStats({ type: "NetworkingConnection", site: "launcher", status: "connected" });

    this._reconnectionAttemptsLeft = totalReconnectionAttempts;

    if (this._reconnectAttemptTimeout) {
      clearTimeout(this._reconnectAttemptTimeout);
      this._reconnectAttemptTimeout = undefined;
    }

    if (!reconnecting) {
      this._clearTimeoutTimer();
      this._clearBackgroundInactivityTimer();
    } else {
      this._skipNextTimeoutReset = true;
    }

    const isPlaygroundServer = getIslandType(this._groupId) === IslandType.Playground;
    this._postMessage(LauncherEventType.NetworkConnected, JSON.stringify({ isPlaygroundServer }));
    this._connected = true;

    this._sendPing();
    this._pingInterval = setInterval(this._sendPing.bind(this), pingTimerInSeconds * 1000) as unknown as number;
  }

  private _disconnect(fireDisconnectEvent: FatalErrors) {
    //Close websocket and remove event listeners
    if (this._room) {
      this._room.removeAllListeners();
      this._room.leave(true);
    }

    //Clear out the websocket
    this._room = undefined;

    if (this._reconnectAttemptTimeout) {
      clearTimeout(this._reconnectAttemptTimeout);
      this._reconnectAttemptTimeout = undefined;
    }

    this._clearTimeoutTimer();
    this._clearBackgroundInactivityTimer();

    this._postMessage(LauncherEventType.NetworkDisconnected);
    this._connected = false;

    //Clear received message indicator
    this._receivedMessage = false;

    ErrorHandling.instance.triggerFatalError(fireDisconnectEvent);
  }

  private _sendPing() {
    if (this._room === undefined) {
      return;
    }

    //Send ping to keep connection alive
    this._room.sendBytes("all", encode({ type: "Ping", launcher: true }) as unknown as ArrayBufferLike);
    this._sentMessageCount++;
    this._pingSentTime = Date.now();
  }

  private _startReconnectAttemptTimer() {
    if (this._reconnectAttemptTimeout !== undefined) {
      return;
    }

    this._reconnectAttemptTimeout = setTimeout(
      () => {
        this._reconnectAttemptTimeout = undefined;
        this._validateTicketAndConnect();
      },
      (reconnectionDelayInSeconds + Math.random() * reconnectionDelayVarianceInSeconds) * 1000
    ) as unknown as number;
  }

  private _onMessage(message: { type: string; data?: string }) {
    switch (message.type) {
      case "Ping":
        this._receivedMessageCount++;
        Logger.logPerfStats({
          type: "Networking",
          site: "launcher",
          sentMessages: this._sentMessageCount,
          receivedMessages: this._receivedMessageCount,
          ping: Date.now() - this._pingSentTime,
          performanceTier: this._perfTier,
          countryCode: this._countryCode,
          serverRegion: this._serverRegion,
          desiredServerRegion: this._desiredServerRegion,
        });
        this._sentMessageCount = 0;
        this._receivedMessageCount = 0;
        break;
      case "ServerShuttingDown":
        console.log("Got server shutting down");
        this._disconnect(FatalErrors.ServerShuttingDown);
        break;
      case "RemovePlayer":
        console.log("Got player removal");
        this._disconnect(FatalErrors.RemovePlayer);
        break;
    }

    this._receivedMessageCount++;
  }

  private _postMessage(type: LauncherEventType, data: string | ArrayBuffer | undefined = undefined) {
    this._iframeEl.contentWindow?.postMessage({ iFrameLauncherEvent, type, data }, "*");
  }

  private _everConnectedToPlaygroundServer = false;

  private _onIFrameMessage(ev: MessageEvent<iFrameLauncherMessage>) {
    if (ev?.data?.iFrameLauncherEvent !== iFrameLauncherEvent) {
      return;
    }

    switch (ev.data.type) {
      case LauncherEventType.NetworkMessage:
        if (ev.data.data) {
          this._onNetworkMessage(ev.data.data as Uint8Array);
        }
        break;
      case LauncherEventType.ChangeIsland:
        if (ev.data.data) {
          const { islandType, islandId } = JSON.parse(ev.data.data as string);
          console.log("Got request to change island with", islandType, islandId);
          this._onChangeIsland(islandType, islandId);
        }
        break;
      case LauncherEventType.Reload:
        this._iframeEl.remove();
        location.reload();
        break;
      case LauncherEventType.IframeLoaded:
        if (this._room && this._connected) {
          const isPlaygroundServer = getIslandType(this._groupId) === IslandType.Playground;
          this._postMessage(LauncherEventType.NetworkConnected, JSON.stringify({ isPlaygroundServer }));
        }
        break;
      case LauncherEventType.SetPerfTier:
        this._perfTier = ev.data.data as number;
        break;
      case LauncherEventType.LostFocus:
        this._resetBackgroundInactivityTimer();
        break;
      case LauncherEventType.GainedFocus:
        this._clearBackgroundInactivityTimer();
        break;
      case LauncherEventType.SwapServers:
        {
          console.log("!!!Switching servers");
          //Parse the ticket data
          if (ev.data.data) {
            const { groupId, ticket } = JSON.parse(ev.data.data as string);
            this._groupId = groupId;
            this._URL.searchParams.set("ticket", ticket);
          } else {
            this._groupId = this._originalGroupId;
            this._URL.searchParams.set("ticket", this._originalTicket);
          }

          //Disconnect from the current server
          this._room?.onLeave.clear();
          if (this._pingInterval) {
            clearInterval(this._pingInterval);
            this._pingInterval = undefined;
          }

          this._postMessage(LauncherEventType.NetworkLosing);
          this._connected = false;

          this._room?.leave(true);

          //Connect to the new server
          this._validateTicketAndConnect();
        }
        break;
    }
  }

  private _onNetworkMessage(data: Uint8Array) {
    if (this._room === undefined) {
      return;
    }

    this._room.sendBytes("all", data as unknown as ArrayBufferLike);
    this._sentMessageCount++;
    this._resetInactivityTimer();
  }

  private _clearBackgroundInactivityTimer() {
    if (this._backgroundTimeoutTimer) {
      clearTimeout(this._backgroundTimeoutTimer);
      this._backgroundTimeoutTimer = undefined;
    }
  }

  private _resetBackgroundInactivityTimer() {
    const env = window._ENV || "local";
    if (env !== "local" && this.loadingComplete && !this._skipNextTimeoutReset) {
      this._clearBackgroundInactivityTimer();

      this._backgroundTimeoutTimer = setTimeout(() => {
        console.log("Got background inactivity timeout");
        this._disconnect(FatalErrors.ServerIdleTimeout);
      }, backgroundTimeoutInSeconds * 1000) as unknown as number;
    }
    this._skipNextTimeoutReset = false;
  }

  private _clearTimeoutTimer() {
    if (this._timeoutTimer) {
      clearTimeout(this._timeoutTimer);
      this._timeoutTimer = undefined;
    }
  }

  private _resetInactivityTimer() {
    const env = window._ENV || "local";
    if (env !== "local" && this.loadingComplete && !this._skipNextTimeoutReset) {
      this._clearTimeoutTimer();

      this._timeoutTimer = setTimeout(() => {
        console.log("Got inactivity timeout");
        this._disconnect(FatalErrors.ServerIdleTimeout);
      }, noActivityTimeoutInSeconds * 1000) as unknown as number;
    }
    this._skipNextTimeoutReset = false;
  }

  private _onChangeIsland(islandType: "class" | "home" | "community" | "personal", islandId: string) {
    const url = new URL(location.href);

    if (mocksResponse()) {
      url.search = getUpdatedLauncherStartupOptions(islandId);
      location.replace(url);
      return;
    }

    if (islandType !== "class") {
      url.search = getUpdatedLauncherStartupOptions(islandId);
      location.replace(url);
      return;
    }

    // call API to get updated island url for target classId
    // (startupParams might be different from current island)

    // Remove islandId type prefix
    const classId = islandId.replace(/^[a-z]+-/, "");
    executeWithRetry(
      () =>
        API.call({
          method: "get",
          path: "/api/monsterverseConfig",
          query: {
            classId,
          },
        }),
      2
    )
      .then((response) => {
        // @ts-expect-error TODO: Property config doesn't exist on response
        url.search = getUpdatedLauncherStartupOptions(islandId, response.config.island.startupParams || {});
        //Do a replace to not keep history (Android navigates "back" to close Monsterverse)
        location.replace(url);
      })
      .catch(() => {
        // fallback to try to open the island with same startup params as current island
        url.search = getUpdatedLauncherStartupOptions(islandId);
        location.replace(url);
      });
  }
}
